import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { formsActions } from '../forms-actions';
import { tap, filter, map } from 'rxjs';
import { selectAccordion, selectForm } from '../forms-selectors';
import { FormScriptingService } from '../../../../features/bizzmine/form/services/scripting/form-scripting.service';
import { CollectionFormService } from '../../../../features/bizzmine/form/services/collection-form.service';
import { concatLatestFrom } from '@ngrx/operators';
import {
  StoreCollectionFormInitializationStatus,
  StoreCollectionFormScriptingStatus,
  storeFormIsIdle
} from '../forms-state';

/**
 * Effects specific to scripting
 */
@Injectable()
export class FormsScriptingEffects {
  private actions$ = inject(Actions);
  public checkOnOpenScripting$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.formInitializationStatusChanged, formsActions.formRefreshed),
    filter(action => {
      if (action.type == "[Forms] formInitializationStatusChanged")
        return action.status == StoreCollectionFormInitializationStatus.Initialized;
      return true;
    }),
    map(({ formId }) => formsActions.performOnOpenScripting({ formId }))
  ));
  private scriptingEffectLock = false;
  public releaseLock$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.scriptingComplete),
    tap(() => this.scriptingEffectLock = false)
  ), { dispatch: false });
  //   }))
  private store$ = inject(Store);
  // ));
  public checkForHiddenRows$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.updateFormData),
    concatLatestFrom(props => [
      this.store$.select(selectForm(props.formId))
    ]),
    map(([props, form]) => {
      if (form !== undefined) {
        form.data.Accordions.forEach(accordion => {
          for (let rowIndex = 0; rowIndex < accordion.Rows.length; rowIndex++) {
            let row = accordion.Rows[rowIndex];
            if (row) {
              let hide = FormScriptingService.hideCompleteRow(row);
              if (hide != row.IsHidden)
                this.store$.dispatch(formsActions.hideRow({
                  formId: props.formId,
                  accordionId: accordion.Id,
                  rowId: rowIndex,
                  hide
                }));
            }
          }

        });
      }
    })
  ), { dispatch: false });
  public checkForHiddenAccordion$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.hideRow),
    concatLatestFrom(props => [
      this.store$.select(selectAccordion(props.formId, props.accordionId))
    ]),
    map(([props, accordion]) => {
      if (accordion !== undefined) {
        let hide = FormScriptingService.hideCompleteAccordion(accordion);
        if (hide !== accordion.IsHidden)
          this.store$.dispatch(formsActions.hideAccordion({
            formId: props.formId,
            accordionId: props.accordionId,
            hide
          }));
      }
    })
  ), { dispatch: false });
  public performScripting$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.scriptingTriggered),
    filter(() => !this.scriptingEffectLock),
    concatLatestFrom(({ formId, fieldId }) => [
      this.store$.select(selectForm(formId))
    ]),
    filter(([props, form]) => {
      // Scripting is only allowed when the form is idle
      return form !== undefined && storeFormIsIdle(form);
    }),
    tap(() => this.scriptingEffectLock = true),
    map(([props, form]) => {
      return formsActions.scriptingEvaluating({ formId: props.formId, onOpen: false });
    })
  ));
  public performOnOpenScripting$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.performOnOpenScripting),
    concatLatestFrom(({ formId }) => [
      this.store$.select(selectForm(formId))
    ]),
    filter(([{ formId }, form]) => {
      return form !== undefined && storeFormIsIdle(form);
    }),
    map(([{ formId }, form]) => formsActions.scriptingEvaluating({ formId, onOpen: true })
    )
  ));
  private formScriptingService = inject(FormScriptingService);
  public startEvaluation$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.scriptingEvaluating),
    concatLatestFrom(({ formId }) => [
      this.store$.select(selectForm(formId))
    ]),
    filter(([props, form]) => {
      return form !== undefined;
    }),
    map(([props, form]) => {
      if (props.onOpen) {
        this.formScriptingService.performOnOpenScripting(props.formId, structuredClone(form!.data));
      } else {
        this.formScriptingService.performScripting(props.formId, structuredClone(form!.data), FormScriptingService.getFormScripts(form!.data));
      }
    })
  ), { dispatch: false });
  public executeActions$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.scriptingExecutingActions),
    concatLatestFrom(({ formId }) => [
      this.store$.select(selectForm(formId))
    ]),
    filter(([props, form]) => {
      return form !== undefined;
    }),
    tap(([props, form]) => this.formScriptingService.executeActions(props.formId, form!.data, props.actions))
  ), { dispatch: false });
  public bumpActionCounter$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.setFieldProperty),
    concatLatestFrom(props => [
      this.store$.select(selectForm(props.formId))
    ]),
    filter(([props, form]) => {
      return form !== undefined && form.status.scripting == StoreCollectionFormScriptingStatus.ExecutingActions;
    }),
    map(([props, form]) => {
      this.formScriptingService.bumpCounter(props.formId);
    })
  ), { dispatch: false });
}